/**
 * Light, dark, or whatever the machine says.
 *
 * A segmented control rather than a single flipping icon, because there are
 * genuinely three states and a one-button toggle cannot show which of them is
 * current: "follow the system" and "pinned to light" look identical on a
 * machine that is currently light, and only one of them changes at sunset.
 */

import { Monitor, Moon, Sun } from "lucide-react"
import * as React from "react"

import { useTheme, type Theme } from "@/lib/theme"
import { cn } from "@/lib/utils"

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
]

/**
 * The keyboard contract, from the WAI-ARIA APG's radio group pattern.
 *
 * This declared `role="radiogroup"` with three `role="radio"` children from the
 * start and then behaved like neither a radio group nor a toolbar: all three
 * were tab stops, and the arrows did nothing. That is worse than an unlabelled
 * control rather than merely different, because the role is a promise: a screen
 * reader announces "Light, radio button, 1 of 3" and the user then presses the
 * arrow key it has just told them to press.
 *
 * Two rules follow, and they are the two halves of the pattern.
 *
 *   ROVING TABINDEX. The group is ONE tab stop, entered at whichever option is
 *   current. Three tab stops here is three of the presses a keyboard user spends
 *   getting past the chrome, on every page, forever - which is the same budget
 *   the skip link was added to protect.
 *
 *   THE ARROWS MOVE AND CHOOSE. In a radio group, moving focus selects; there is
 *   no separate commit key. That is exactly what distinguishes a radio group
 *   from a toolbar, and it is why the arrow handler calls `setTheme` rather than
 *   only moving focus. Harmless here in a way it would not be elsewhere: the
 *   consequence of stepping through is a theme flip, which is instantly visible
 *   and instantly reversible by stepping back.
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  const buttons = React.useRef<(HTMLButtonElement | null)[]>([])

  function choose(index: number) {
    const option = OPTIONS[index]
    if (!option) return
    setTheme(option.value)
    // Focus follows the selection rather than staying put: after an arrow the
    // roving tab stop has moved, and focus has to move with it or the next
    // arrow press would step from the wrong place.
    buttons.current[index]?.focus()
  }

  // `from` is the index of the button the key was actually pressed on, not the
  // index of the checked option. The two are the same whenever a person has
  // arrived here by keyboard, since moving selects; they diverge the moment
  // anything focuses a radio programmatically, and stepping from the checked
  // one would then jump the cursor somewhere the user was not.
  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, from: number) {
    // Down and Up as well as Right and Left: the APG asks for both pairs, and
    // this control is read as a vertical list by anyone who cannot see that it
    // is laid out in a row.
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0
    if (step !== 0) {
      event.preventDefault()
      // Wrapping, so neither end of the group is a dead edge.
      choose((from + step + OPTIONS.length) % OPTIONS.length)
      return
    }
    if (event.key === "Home") {
      event.preventDefault()
      choose(0)
    } else if (event.key === "End") {
      event.preventDefault()
      choose(OPTIONS.length - 1)
    }
  }

  return (
    <div
      className="flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5"
      role="radiogroup"
      aria-label="Colour theme"
    >
      {OPTIONS.map(({ value, label, icon: Icon }, index) => (
        <button
          key={value}
          ref={(node) => {
            buttons.current[index] = node
          }}
          type="button"
          role="radio"
          aria-checked={theme === value}
          aria-label={label}
          title={label}
          tabIndex={theme === value ? 0 : -1}
          onClick={() => setTheme(value)}
          onKeyDown={(event) => onKeyDown(event, index)}
          className={cn(
            "grid h-8 w-8 place-items-center rounded-md transition-colors",
            theme === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </button>
      ))}
    </div>
  )
}
